import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Webhook } from 'lucide-react';
import { formatDate, formatTime } from '@/shared/lib/format';
import { useConfirm } from '@/shared/components/confirm';
import { PageHeader } from '@/shared/components/page-header';
import { ActionIconButton } from '@/shared/components/action-icon-button';
import { Card } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { Skeleton } from '@/shared/ui/skeleton';
import type { WebhookType } from '@/shared/types/nps';
import { WEBHOOK_LABEL, WEBHOOK_TYPES } from '../constants/integrations';
import { createWebhook, deleteWebhook, useIntegration, useIntegrationEvents } from '../api/use-nps';
import { ProviderLogo } from './provider-logo';
import { formatBrl } from '../utils/format-brl';

export function IntegrationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { data: integ, isLoading } = useIntegration(id);
  const eventsQuery = useIntegrationEvents(id);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['nps', 'integration', id] });

  const addWebhook = async (type: WebhookType) => {
    if (!id) return;
    try {
      await createWebhook({ integration_id: id, type });
      toast.success('Webhook adicionado.');
      refresh();
    } catch {
      toast.error('Falha ao adicionar webhook.');
    }
  };

  const removeWebhook = async (whId: string, type: WebhookType) => {
    if (!id) return;
    if (type === 'UNINSTALLED') {
      toast.error('Webhook de desinstalação não pode ser removido.');
      return;
    }
    const ok = await confirm({ title: 'Remover webhook', description: 'Deseja remover este webhook?', confirmLabel: 'Remover', variant: 'destructive' });
    if (!ok) return;
    try {
      await deleteWebhook(id, whId);
      toast.success('Webhook removido.');
      refresh();
    } catch {
      toast.error('Falha ao remover.');
    }
  };

  if (isLoading || !integ) {
    return (
      <>
        <PageHeader title="Integração" actions={<Button variant="secondary" onClick={() => navigate('/nps/integracoes')}><ArrowLeft className="size-4" /> Voltar</Button>} />
        <Skeleton className="h-64 rounded-2xl" />
      </>
    );
  }

  const events = eventsQuery.data ?? [];
  const webhooks = integ.integration_webhooks ?? [];
  const existingTypes = new Set(webhooks.map((w) => w.type));

  return (
    <>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <motion.div initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }}>
            <ProviderLogo type={integ.provider.type} size={52} />
          </motion.div>
          <div className="min-w-0">
            <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
              {integ.provider.name}
              <Badge variant="success" className="font-normal">Ativa</Badge>
            </h1>
            {integ.store_url ? <p className="mt-1 truncate text-sm text-muted-foreground">{integ.store_url}</p> : null}
          </div>
        </div>
        <Button variant="secondary" onClick={() => navigate('/nps/integracoes')}>
          <ArrowLeft className="size-4" /> Voltar
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="space-y-4 p-5">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">Webhooks</h2>
            <Webhook className="size-4 text-muted-foreground" />
          </div>
          {webhooks.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum webhook configurado.</p>
          ) : (
            <ul className="space-y-2">
              {webhooks.map((w) => (
                <li key={w.id} className="rounded-xl border border-border/50 px-3 py-2">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium">{WEBHOOK_LABEL[w.type]}</p>
                      <p className="text-xs text-muted-foreground">{WEBHOOK_TYPES[w.type].description}</p>
                    </div>
                    {w.type !== 'UNINSTALLED' ? (
                      <ActionIconButton label="Remover webhook" onClick={() => removeWebhook(w.id, w.type)} className="text-destructive">
                        <Trash2 className="size-4" />
                      </ActionIconButton>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className="flex flex-wrap gap-2">
            {(['ORDER_PAID', 'UNINSTALLED'] as const).map((t) =>
              existingTypes.has(t) ? null : (
                <Button key={t} variant="secondary" size="sm" onClick={() => addWebhook(t)}>
                  <Plus className="size-4" /> {WEBHOOK_LABEL[t]}
                </Button>
              ),
            )}
          </div>
        </Card>

        <Card className="p-5 lg:col-span-2">
          <h2 className="mb-4 text-sm font-semibold">Eventos recebidos</h2>
          {eventsQuery.isLoading ? (
            <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 rounded-lg" />)}</div>
          ) : events.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum evento ainda.</p>
          ) : (
            <ul className="divide-y divide-border/40">
              {events.map((e) => (
                <li key={e.id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{e.event_id}</p>
                    <p className="text-xs text-muted-foreground">
                      {WEBHOOK_LABEL[e.type as WebhookType] ?? e.type} · {formatDate(e.created_at)} {formatTime(e.created_at)}
                      {e.body?.total != null ? ` · ${formatBrl(e.body.total)}` : ''}
                    </p>
                  </div>
                  <Badge variant={e.status ? 'success' : 'destructive'} className="font-normal">{e.status ? 'OK' : 'Falha'}</Badge>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </>
  );
}
